import { useNavigate } from 'react-router-dom';
import { AppNavigationState, SidePanelDetails } from 'src/components/context/AppContextModels';
import { ShowModalInfo } from 'src/components/generic-components/ConfirmationModal';
import { createItemMetadata, updateItemMetadata } from 'src/utilities/ItemMetadata';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationBannerConfig, HomepageEntity, HomePageWizard } from './HomepageModel';
import HomePageContactsStep from './steps/HomePageContactsStep';
import HomePageContentStep1 from './steps/HomePageStep1';

export const DEFAULT_SIDE_PANEL_DETAILS: SidePanelDetails = {
  keyContacts: {
    isEnabled: false,
    title: 'Key Contacts',
    isExpandedByDefault: false,
    contentType: 'rich-text-editor',
    richTextContent: ''
  },
  officeHours: {
    isEnabled: false,
    title: 'Office Hours',
    isExpandedByDefault: false,
    contentType: 'rich-text-editor',
    richTextContent: ''
  },
  supportLinks: {
    id: uuidv4(),
    isEnabled: false,
    title: 'Support Links',
    isExpandedByDefault: false,
    contentType: 'table',
    richTextContent: null,
    tableContent: []
  },
  customSidePanelWidgets: []
};

export const DEFAULT_APPLICATION_BANNER: ApplicationBannerConfig = {
  isEnabled: false,
  bannerType: 'info',
  header: '',
  content: '',
  dismissible: true
};

export const getHomePageContentDefaultState = (alias: string): HomepageEntity => ({
  id: uuidv4(),
  hasWelcomeContent: true,
  welcomeTitle: 'Welcome',
  welcomeContentDetails: [],
  applicationBanner: DEFAULT_APPLICATION_BANNER,
  contactsWidgetContacts: [],
  itemMetadata: createItemMetadata(alias)
});

export const getTeamPageContentDefaultState = (alias: string): HomePageWizard => ({
  id: uuidv4(),
  homePageDetailsAndWidgets: {
    hasWelcomeContent: false,
    welcomeTitle: 'Welcome',
    welcomeContentDetails: [],
    applicationBanner: DEFAULT_APPLICATION_BANNER,
    contactsWidgetContacts: []
  },
  itemMetadata: createItemMetadata(alias)
});

export const HomepageWizardSteps: any[] = [
  {
    title: 'Homepage Details & Widgets',
    description: '',
    stateKey: 'homepageDetails',
    StepContent: HomePageContentStep1,
    isOptional: false
  },
  {
    title: 'Contacts Widget',
    description: 'Configure the Key Contacts displayed on the homepage',
    stateKey: 'contactsWidget',
    StepContent: HomePageContactsStep,
    isOptional: false
  }
];

export const closeModal = (setShowModalInfo: React.Dispatch<React.SetStateAction<ShowModalInfo>>) => {
  setShowModalInfo({
    showModal: false,
    eventHeader: '',
    eventContentType: ''
  });
};

export const homepageNavigateBackToFrom = (message: string, content: string, navigatePath: string, navigate: ReturnType<typeof useNavigate>) => {
  const navigationStateMessage: AppNavigationState =
    message === 'success'
      ? {
          message: {
            type: 'success',
            content: content,
            header: ''
          }
        }
      : {};

  navigate(navigatePath, { state: navigationStateMessage });
};

export const homepageContentAPIToWizard = (homepageEntity: HomepageEntity): HomePageWizard => {
  return {
    id: homepageEntity.id,
    homePageDetailsAndWidgets: {
      hasWelcomeContent: homepageEntity.hasWelcomeContent,
      welcomeTitle: homepageEntity.welcomeTitle,
      welcomeContentDetails: homepageEntity.welcomeContentDetails,
      applicationBanner: homepageEntity.applicationBanner || DEFAULT_APPLICATION_BANNER,
      contactsWidgetContacts: homepageEntity.contactsWidgetContacts || []
    },
    itemMetadata: homepageEntity.itemMetadata
  };
};

export const homepageContentWizardToAPI = (homepageWizard: HomePageWizard, alias: string): HomepageEntity => {
  const up = updateItemMetadata(alias, homepageWizard.itemMetadata);
  return {
    id: homepageWizard.id,
    hasWelcomeContent: homepageWizard.homePageDetailsAndWidgets.hasWelcomeContent,
    welcomeTitle: homepageWizard.homePageDetailsAndWidgets.welcomeTitle,
    welcomeContentDetails: homepageWizard.homePageDetailsAndWidgets.welcomeContentDetails,
    applicationBanner: homepageWizard.homePageDetailsAndWidgets.applicationBanner,
    contactsWidgetContacts: homepageWizard.homePageDetailsAndWidgets.contactsWidgetContacts || [],
    itemMetadata: up
  };
};
